import {
    APP_INITIALIZER,
    enableProdMode,
    importProvidersFrom,
} from '@angular/core';

import { init } from './app/app.module';
import { environment } from './environments/environment';
import { AppComponent, INSTITUTION_CODE } from './app/app.component';
import { EsploroMissingTranslationHandler } from './app/missing-translation-handler';
import { UrlUtils } from './app/shared/utils/url.utils';
import { RestApiBase } from './app/shared/configurations/rest-api-base.config';
import { map, Observable } from 'rxjs';
import {
    MissingTranslationHandler,
    TranslateLoader,
    TranslateModule,
} from '@ngx-translate/core';
import { MuloMatCssVarsModule } from '@exl-ng/mulo-common';
import { provideAnimations } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app/app-routing.module';
import { BidiModule } from '@angular/cdk/bidi';
import { JwtModule } from '@auth0/angular-jwt';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import {
    HttpClient,
    provideHttpClient,
    withInterceptors,
    withInterceptorsFromDi,
    withXsrfConfiguration,
} from '@angular/common/http';
import { customTooltipDefaults } from './app/shared/a11y/mat-tooltip-default-option';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import {
    MomentDateAdapter,
    provideMomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateTimeService } from './app/core/date-time.service';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from '@angular/material/core';
import { ConfigService } from './app/config.service';
import { institutionInterceptor } from './app/core/institution.interceptor';
import { SharedModule } from './app/shared/shared.module';
import { EsploroHeaderModule } from './app/esploro-header/esploro-header.module';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from "ng-recaptcha";
import {ConfigurationHandlerService} from "./app/core/configuration-handler.service";

class CustomLoader implements TranslateLoader {
    constructor(private http: HttpClient) {}

    getTranslation(lang: string): Observable<any> {
        const stored = JSON.parse(
            localStorage.getItem('rvXl8n_' + lang) || '{}',
        );
        return this.http
            .get(
                `${RestApiBase.TRANSLATIONS}?institution=${UrlUtils.getParam(
                    'institution',
                )}&lang=${lang}`,
            )
            .pipe(map((remoteData) => ({ ...stored, ...remoteData })));
    }
}

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            BrowserModule,
            JwtModule.forRoot({
                config: {
                    tokenGetter: () =>
                        localStorage.getItem('access_token') ||
                        sessionStorage.getItem('access_token'),
                },
            }),
            BidiModule,
            AppRoutingModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useClass: CustomLoader,
                    deps: [HttpClient],
                },
                missingTranslationHandler: {
                    provide: MissingTranslationHandler,
                    useClass: EsploroMissingTranslationHandler,
                },
                useDefaultLang: false,
            }),
            MuloMatCssVarsModule.forRoot({
                isAutoContrast: true,
                darkThemeClass: 'isDarkTheme',
                lightThemeClass: 'isLightTheme',
            }),
            SharedModule,
            RecaptchaV3Module,
        ),
        {
            provide: APP_INITIALIZER,
            useFactory: init,
            multi: true,
            deps: [ConfigService],
        },
        {
            provide: MAT_DATE_FORMATS,
            deps: [DateTimeService],
            useFactory: (dateTimeService: DateTimeService) =>
                dateTimeService.getDateFormat(),
        },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, DateTimeService],
        },
        {
            provide: INSTITUTION_CODE,
            useFactory: () => UrlUtils.getParam('institution'),
        },
        provideMomentDateAdapter(undefined, { useUtc: true }),
        {
            provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
            useValue: customTooltipDefaults,
        },
        provideHttpClient(
            withInterceptors([institutionInterceptor]),
            withInterceptorsFromDi(),
            withXsrfConfiguration({}),
        ),
        provideAnimations(),
        {
            provide: RECAPTCHA_V3_SITE_KEY,
            useFactory: (configHandlerSvc: ConfigurationHandlerService) => configHandlerSvc.getReCaptchaV3SiteKey(),
            deps: [ConfigurationHandlerService],
        }
    ],
}).catch((err) => console.error(err));
